import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Eye, Check, X, Pencil, Plus, Minus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import DataTable from "@/components/DataTable";

export default function Bills() {
  const { user, hasPerm } = useAuth();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cancelOpen, setCancelOpen] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [editOpen, setEditOpen] = useState(null);
  const [editCart, setEditCart] = useState([]);
  const [editCustomer, setEditCustomer] = useState("");
  const [editDiscount, setEditDiscount] = useState("0");
  const [reloadKey, setReloadKey] = useState(0);
  const load = () => setReloadKey((k) => k + 1);

  // Load supporting data (products + customers) once for the edit dialog dropdowns.
  useEffect(() => {
    (async () => {
      const [p, c] = await Promise.all([
        api.get("/products").catch(() => ({ data: [] })),
        api.get("/customers").catch(() => ({ data: [] })),
      ]);
      // Both legacy (array) and paginated ({rows, total}) shapes are accepted.
      setProducts(Array.isArray(p.data) ? p.data : (p.data.rows || []));
      setCustomers(Array.isArray(c.data) ? c.data : (c.data.rows || []));
    })();
  }, []);

  const canActHeldBill = (bill) => {
    if (user.role === "super_admin") return true;
    if (user.role === "admin" && (user.branch_ids || []).includes(bill.branch_id)) return true;
    if (user.role === "cashier" && bill.cashier_id === user.id) return true;
    return false;
  };

  const openEdit = (bill) => {
    // Build editable cart from bill items
    const cart = [];
    for (const it of bill.items || []) {
      const prod = products.find((p) => p.id === it.product_id);
      if (prod) cart.push({ product: prod, quantity: it.quantity });
    }
    setEditCart(cart);
    setEditCustomer(bill.customer_id || "walkin");
    setEditDiscount(String(bill.discount_percent || 0));
    setEditOpen(bill);
  };

  const saveEdit = async () => {
    try {
      await api.put(`/sales-bills/${editOpen.id}/hold-edit`, {
        items: editCart.map((it) => ({ product_id: it.product.id, quantity: it.quantity })),
        customer_id: editCustomer === "walkin" ? null : editCustomer,
        discount_percent: parseFloat(editDiscount) || 0,
      });
      toast.success("Held bill updated");
      setEditOpen(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const confirmHeld = async (bill) => {
    if (!window.confirm(`Confirm bill ${bill.bill_number}? This will decrement stock.`)) return;
    try {
      await api.post(`/sales-bills/${bill.id}/hold-confirm`);
      toast.success(`Confirmed ${bill.bill_number}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cancelHeld = async () => {
    if (!cancelReason.trim()) { toast.error("Reason is required"); return; }
    try {
      await api.post(`/sales-bills/${cancelOpen.id}/hold-cancel`, { reason: cancelReason });
      toast.success("Bill cancelled");
      setCancelOpen(null); setCancelReason(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const branchProducts = editOpen ? products.filter((p) => p.branch_id === editOpen.branch_id) : [];
  const branchCustomers = editOpen ? customers.filter((c) => c.branch_id === editOpen.branch_id) : [];

  const addProdToEdit = (pid) => {
    const p = branchProducts.find((x) => x.id === pid);
    if (!p) return;
    setEditCart((prev) => {
      const idx = prev.findIndex((it) => it.product.id === pid);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
        return next;
      }
      return [...prev, { product: p, quantity: 1 }];
    });
  };

  const billColumns = [
    { key: "bill_number", label: "Bill #", sortable: true, render: (b) => <span className="font-mono text-xs">{b.bill_number}</span> },
    { key: "created_at", label: "Date", sortable: true, render: (b) => <span className="text-slate-600">{new Date(b.created_at).toLocaleString("en-IN")}</span> },
    { key: "customer_name", label: "Customer", render: (b) => b.customer_name || "Walk-in" },
    { key: "cashier_name", label: "Cashier", render: (b) => <span className="text-slate-600">{b.cashier_name}</span> },
    { key: "status", label: "Status", render: (b) => {
      if (b.cancelled) return <Badge variant="destructive" className="rounded-none">Cancelled</Badge>;
      if (b.on_hold) return <Badge className="rounded-none bg-amber-500">On hold</Badge>;
      return <Badge className="rounded-none bg-green-600">Completed</Badge>;
    } },
    { key: "payment_method", label: "Payment", render: (b) => <span className="text-xs">{b.payment_method || "—"}</span> },
    { key: "grand_total", label: "Total", sortable: true, className: "text-right", render: (b) => <span className="font-semibold tabular-nums">{formatINR(b.grand_total)}</span> },
    { key: "imported_from_tally", label: "Src", render: (b) => b.imported_from_tally ? <span className="text-[10px] uppercase text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">Tally</span> : <span className="text-[10px] text-slate-400">Live</span> },
  ];
  const billFilters = [
    { key: "payment_method", type: "select", label: "Payment", options: [
      { value: "Cash", label: "Cash" }, { value: "Card", label: "Card" }, { value: "UPI", label: "UPI" },
      { value: "Credit", label: "Credit" }, { value: "Split", label: "Split" },
    ] },
    { key: "on_hold", type: "select", label: "Hold", options: [{ value: "true", label: "On hold only" }, { value: "false", label: "Posted only" }] },
    { key: "imported", type: "select", label: "Source", options: [{ value: "true", label: "Imported (Tally)" }, { value: "false", label: "Live only" }] },
    { key: "date_from", type: "date", label: "From date" },
    { key: "date_to", type: "date", label: "To date" },
  ];

  return (
    <div className="space-y-6" data-testid="bills-page">
      <h1 className="text-2xl font-semibold tracking-tight">Sales Bills</h1>
      <DataTable
        endpoint="/sales-bills"
        columns={billColumns}
        filters={billFilters}
        searchPlaceholder="Search bill #, customer, phone, cashier..."
        reloadKey={reloadKey}
        actions={(b) => {
          const isHeld = b.on_hold && !b.cancelled;
          return (
            <div className="flex justify-end items-center gap-1">
              <Link to={`/bills/${b.id}`} className="inline-flex items-center gap-1 text-slate-900 hover:text-blue-600 text-sm" data-testid={`view-bill-${b.id}`}>
                <Eye className="w-4 h-4" /><span className="hidden md:inline">View</span>
              </Link>
              {isHeld && canActHeldBill(b) && (
                <>
                  <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-slate-700"
                    onClick={() => openEdit(b)} data-testid={`held-edit-${b.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-green-700"
                    onClick={() => confirmHeld(b)} data-testid={`held-confirm-${b.id}`}><Check className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-red-600"
                    onClick={() => { setCancelOpen(b); setCancelReason(""); }} data-testid={`held-cancel-${b.id}`}><X className="w-3.5 h-3.5" /></Button>
                </>
              )}
            </div>
          );
        }}
        dataTestid="bills-table"
      />

      {/* Cancel held bill dialog */}
      <Dialog open={!!cancelOpen} onOpenChange={(v) => { if (!v) { setCancelOpen(null); setCancelReason(""); } }}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle>Cancel held bill {cancelOpen?.bill_number}</DialogTitle></DialogHeader>
          <div>
            <Label>Reason (required)</Label>
            <Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Customer changed mind / duplicate / etc." className="rounded-none mt-1" data-testid="hold-cancel-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setCancelOpen(null)}>Back</Button>
            <Button className="bg-red-600 hover:bg-red-700 rounded-none" onClick={cancelHeld} data-testid="hold-cancel-confirm">Cancel Bill</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit held bill dialog */}
      <Dialog open={!!editOpen} onOpenChange={(v) => { if (!v) setEditOpen(null); }}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>Edit held bill {editOpen?.bill_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Customer</Label>
              <select className="w-full border border-slate-200 rounded-none p-2 text-sm mt-1" value={editCustomer}
                onChange={(e) => setEditCustomer(e.target.value)} data-testid="held-edit-customer">
                <option value="walkin">Walk-in Customer</option>
                {branchCustomers.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.phone}</option>)}
              </select>
            </div>
            <div>
              <Label>Add item</Label>
              <select className="w-full border border-slate-200 rounded-none p-2 text-sm mt-1"
                onChange={(e) => { if (e.target.value) { addProdToEdit(e.target.value); e.target.value = ""; } }}
                data-testid="held-edit-add-item">
                <option value="">+ Select a product to add…</option>
                {branchProducts.map((p) => <option key={p.id} value={p.id}>{p.name} • Stock {p.stock}</option>)}
              </select>
            </div>
            <div className="border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200"><tr><th className="p-2 text-left">Item</th><th className="p-2 text-center">Qty</th><th /></tr></thead>
                <tbody>
                  {editCart.length === 0 && <tr><td colSpan={3} className="p-4 text-center text-slate-400">No items</td></tr>}
                  {editCart.map((it) => (
                    <tr key={it.product.id} className="border-b border-slate-100">
                      <td className="p-2">
                        <div className="font-medium">{it.product.name}</div>
                        <div className="text-xs text-slate-500">{formatINR(it.product.selling_price || (it.product.cost_price * (1 + (it.product.margin_percent || 0) / 100)))}</div>
                      </td>
                      <td className="p-2">
                        <div className="flex items-center justify-center gap-1">
                          <Button type="button" size="icon" variant="ghost" className="h-6 w-6 rounded-none"
                            onClick={() => setEditCart((prev) => prev.flatMap((x) => x.product.id !== it.product.id ? [x] : (x.quantity - 1 <= 0 ? [] : [{ ...x, quantity: x.quantity - 1 }])))}>
                            <Minus className="w-3 h-3" />
                          </Button>
                          <span className="w-6 text-center">{it.quantity}</span>
                          <Button type="button" size="icon" variant="ghost" className="h-6 w-6 rounded-none"
                            onClick={() => setEditCart((prev) => prev.map((x) => x.product.id === it.product.id ? { ...x, quantity: x.quantity + 1 } : x))}>
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                      <td className="p-2 text-right">
                        <Button type="button" size="icon" variant="ghost" className="h-6 w-6 rounded-none text-red-600"
                          onClick={() => setEditCart((prev) => prev.filter((x) => x.product.id !== it.product.id))}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasPerm("discount_apply") && (
              <div>
                <Label>Discount %</Label>
                <Input type="number" min="0" max="100" step="0.1" value={editDiscount}
                  onChange={(e) => setEditDiscount(e.target.value)} className="rounded-none mt-1" data-testid="held-edit-discount" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setEditOpen(null)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800 rounded-none" onClick={saveEdit} disabled={editCart.length === 0} data-testid="held-edit-save">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
